import React, { Component } from 'react';
import intl from 'react-intl-universal';
import { isMobile, isTablet } from 'react-device-detect';
import PropTypes from 'prop-types';
import styled from 'src/ui/library/styled';
import classNames from 'classnames';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const ITEM_HEIGHT = 48;
const MENU_Z_INDEX = 100000;
const SELECTED_OPTION_BACKGROUND = 'rgba(0, 0, 0, 0.14)';
const FOCUSED_OPTION_BACKGROUND = 'rgba(0, 0, 0, 0.08)';

const isNil = value => value === null || typeof value === 'undefined';

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

const isEmptyValue = value =>
  isNil(value) ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isObject(value) && Object.prototype.hasOwnProperty.call(value, 'value') && isEmptyValue(value.value));

const valuesEqual = (left, right) => {
  if (left === right) return true;
  if (isNil(left) || isNil(right)) return false;

  return left.toString() === right.toString();
};

const styles = (theme, props) => {
  const isBigLabel = false;
  const textAlign = props.align === 'C' ? 'center' : props.align === 'R' ? 'right' : 'left';

  return {
    root: {
      position: 'relative',
      width: '100%',
      maxWidth: '300px',
      display: 'inline-block',
      border: 'solid 1px transparent',
      backgroundColor: theme.colors.eyGrey4,
      fontFamily: '"EYInterstate", sans-serif',
      overflow: 'visible',
    },
    enableDropdownField: {
      borderBottom: '1px solid #2E2E38',
      '&:hover, &:focus-within': {
        background: '#E1E1E6',
      },
    },
    labelPlaceholder: {
      // height: isBigLabel ? '24px' : '16px',
    },
    floatLabel: {
      position: 'absolute',
      top: 'calc(50% - 8px)',
      width: 'calc(100% - 32px)',
      left: '8px',
      transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, font-size 0ms',
      transformOrigin: 'top left',
      color: theme.colors.lightGrey,
      fontSize: '1rem',
      // zIndex: 1,
      pointerEvents: 'none',
      ...(props.useEllipsisLabel && {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }),
    },
    mobileShrinkLabel: {
      top: '15px',
    },
    floatLabelShrink: {
      transform: `translate(0, -${isBigLabel ? 16 : 8}px)`,
      fontSize: '0.8em',
      color: theme.colors.eyGrey14,
    },
    focused: {},
    hasError: {
      borderBottom: `solid 1px ${theme.colors.red}`,
    },
    disabled: {
      color: '#aa9fa2',
    },
    autocomplete: {
      width: '100%',
      height: `${ITEM_HEIGHT}px`,
      '& .MuiAutocomplete-inputRoot': {
        minHeight: `${ITEM_HEIGHT}px`,
        height: `${ITEM_HEIGHT}px`,
        padding: '16px 32px 0 8px !important',
        alignItems: 'center',
        backgroundColor: theme.colors.eyGrey4,
        border: '1px solid transparent',
        borderRadius: 0,
        color: theme.colors.eyConfidentGrey,
        fontFamily: '"EYInterstate", sans-serif',
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: '20px',
        boxSizing: 'border-box',
        '&.Mui-focused': {
          borderColor: theme.colors.eyGrey6,
        },
        '&:hover': {
          borderColor: 'transparent',
        },
      },
      '& .MuiAutocomplete-input': {
        minWidth: '0 !important',
        padding: '0 !important',
        height: '20px',
        color: theme.colors.eyConfidentGrey,
        fontFamily: '"EYInterstate", sans-serif',
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: '20px',
        textAlign,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiAutocomplete-endAdornment': {
        // top: 'calc(50% - 14px)',
        // right: '4px !important',
      },
      '& .MuiAutocomplete-clearIndicator': {
        display: 'none',
      },
    },
    popper: {
      zIndex: MENU_Z_INDEX,
    },
    paper: {
      marginTop: 0,
      marginBottom: 0,
      backgroundColor: '#FFFFFF',
      border: `1px solid ${theme.colors.eyGrey6}`,
      borderRadius: 0,
      boxShadow: 'none',
      overflow: 'hidden',
    },
    listbox: {
      maxHeight: ITEM_HEIGHT * 4.5 - 2,
      paddingTop: '0 !important',
      paddingBottom: '0 !important',
      overflowX: 'hidden',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
    option: {
      height: 'auto',
      minHeight: `${ITEM_HEIGHT}px`,
      padding: '0 12px !important',
      color: theme.colors.eyConfidentGrey,
      fontFamily: '"EYInterstate", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '20px',
      boxSizing: 'border-box',
      transition: 'none',
      '&.Mui-focused, &:hover': {
        backgroundColor: `${theme.colors.eyGrey4} !important`,
      },
      '&[aria-selected="true"], &[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
        backgroundColor: `${SELECTED_OPTION_BACKGROUND} !important`,
      },
      '&:active': {
        backgroundColor: `${FOCUSED_OPTION_BACKGROUND} !important`,
      },
      '& .autocomplete-item-wrapper': {
        width: '100%',
        minHeight: `${ITEM_HEIGHT}px`,
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.eyConfidentGrey,
        fontFamily: '"EYInterstate", sans-serif',
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: '20px',
        overflow: 'visible',
      },
      '& .autocomplete-item-name': {
        maxWidth: '100%',
        overflow: 'visible',
        overflowWrap: 'break-word',
        textOverflow: 'clip',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
    optionAdditionalInfo: {
      height: 'auto',
      minHeight: `${ITEM_HEIGHT}px`,
      paddingTop: '10px !important',
      paddingBottom: '10px !important',
      '& .autocomplete-item-wrapper': {
        height: 'auto',
        minHeight: '28px',
        alignItems: 'flex-start',
      },
      '& .availableEmail': {
        margin: 0,
      },
    },
    popupIndicator: {
      width: '32px',
      height: '32px',
      padding: '0 !important',
      color: `${theme.colors.eyConfidentGrey} !important`,
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    popupIndicatorOpen: {
      transform: 'rotate(180deg)',
    },
    noOptions: {
      minHeight: `${ITEM_HEIGHT}px`,
      padding: '14px 12px',
      color: theme.colors.eyConfidentGrey,
      fontFamily: '"EYInterstate", sans-serif',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '20px',
    },
  };
};

class DropdownBase extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
    additionalInfoDropdown: PropTypes.bool,
    align: PropTypes.string,
    className: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.array]),
    hasError: PropTypes.bool,
    hideIconOnSelect: PropTypes.bool,
    id: PropTypes.string,
    isClearable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isOptionHidden: PropTypes.func,
    isSearchable: PropTypes.bool,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFilter: PropTypes.func,
    onFocus: PropTypes.func,
    onMenuClose: PropTypes.func,
    onMenuOpen: PropTypes.func,
    openMenuOnFocus: PropTypes.bool,
    openOnClick: PropTypes.bool,
    openOnFocus: PropTypes.bool,
    options: PropTypes.array,
    renderIcon: PropTypes.func,
    renderOption: PropTypes.func,
    required: PropTypes.bool,
    useEllipsisLabel: PropTypes.bool,
    usePortal: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.array]),
  };

  static defaultProps = {
    additionalInfoDropdown: false,
    align: 'L',
    className: '',
    defaultValue: null,
    hasError: false,
    hideIconOnSelect: false,
    id: null,
    isClearable: false,
    isDisabled: false,
    isLoading: false,
    isOptionHidden: null,
    isSearchable: true,
    name: null,
    onBlur: null,
    onChange: null,
    onFilter: null,
    onFocus: null,
    onMenuClose: null,
    onMenuOpen: null,
    openMenuOnFocus: undefined,
    openOnClick: false,
    openOnFocus: false,
    options: [],
    renderIcon: null,
    renderOption: null,
    required: false,
    useEllipsisLabel: false,
    usePortal: true,
    value: undefined,
  };

  constructor(props) {
    super(props);
    const initialValue = typeof props.value === 'undefined' ? props.defaultValue : props.value;

    this.state = {
      isFocused: !isEmptyValue(initialValue),
      menuOpen: false,
      searchText: '',
      value: typeof initialValue === 'undefined' ? null : initialValue,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ value: typeof this.props.value === 'undefined' ? null : this.props.value });
    }
  }

  getOptionValue = option => {
    if (isObject(option) && Object.prototype.hasOwnProperty.call(option, 'value')) return option.value;

    return option;
  };

  getOptionLabel = option => {
    if (isNil(option)) return '';
    if (isObject(option)) {
      if (!isNil(option.label)) return option.label.toString();
      if (!isNil(option.displayText)) return option.displayText.toString();
      if (!isNil(option.name)) return option.name.toString();
      if (!isNil(option.value)) return option.value.toString();
    }

    return option.toString();
  };

  getOptionKey = option => {
    if (isNil(option)) return '';

    if (isObject(option)) {
      const keyParts = ['value', 'id', 'key', 'delegateProfileKey', 'profileKey', 'userId', 'email', 'label', 'displayText', 'name']
        .map(prop => option[prop])
        .filter(value => !isNil(value) && value !== '')
        .map(value => value.toString());

      if (keyParts.length) return keyParts.join('|');
    }

    return this.getOptionLabel(option);
  };

  getSelectedOption = () => {
    const { options } = this.props;
    const { value } = this.state;

    if (isEmptyValue(value)) return null;
    if (isObject(value)) return value;

    return (options || []).find(option => valuesEqual(this.getOptionValue(option), value)) || null;
  };

  filterOptions = (options, state) => {
    const { isOptionHidden, onFilter } = this.props;
    const inputValue = state.inputValue || '';
    const visibleOptions = isOptionHidden ? options.filter(option => !isOptionHidden(option)) : options;

    if (onFilter) return visibleOptions.filter(option => onFilter(option, inputValue));
    if (!inputValue) return visibleOptions;

    return visibleOptions.filter(option => this.getOptionLabel(option).toLowerCase().indexOf(inputValue.toLowerCase()) === 0);
  };

  handleChange = (event, selectedValue) => {
    const selectedPayload = isObject(selectedValue) ? selectedValue : {};
    const retValue = selectedValue === null ? null : this.getOptionValue(selectedValue);
    const nextValue = isEmptyValue(retValue) ? null : selectedValue;

    this.setState({ value: nextValue }, () => {
      if (this.props.onChange) {
        this.props.onChange({
          target: {
            name: this.props.name,
            id: this.props.id,
            ...selectedPayload,
            value: retValue,
          },
        });
      }
    });
  };

  handleInputChange = (event, searchText) => {
    this.setState({ searchText });
  };

  handleMenuOpen = () => {
    this.setState({ menuOpen: true });

    if (typeof this.props.onMenuOpen === 'function') this.props.onMenuOpen();
  };

  handleMenuClose = () => {
    this.setState({ menuOpen: false });

    if (typeof this.props.onMenuClose === 'function') this.props.onMenuClose();
  };

  toggleFocus = (isFocused, event) => {
    this.setState({ isFocused }, () => {
      if (isFocused && typeof this.props.onFocus === 'function') this.props.onFocus(event);
      if (!isFocused && typeof this.props.onBlur === 'function') this.props.onBlur(event);
    });
  };

  renderOption = (optionProps, option) => {
    const { additionalInfoDropdown, classes, renderOption } = this.props;
    const { searchText } = this.state;
    const content = renderOption ? renderOption(option, searchText) : this.getOptionLabel(option);

    return (
      <li {...optionProps} className={classNames(optionProps.className, { [classes.optionAdditionalInfo]: additionalInfoDropdown })}>
        {content}
      </li>
    );
  };

  renderPopupIcon = () => {
    const { hideIconOnSelect, isDisabled, renderIcon } = this.props;
    const { menuOpen } = this.state;
    const selectedOption = this.getSelectedOption();

    if (hideIconOnSelect && selectedOption !== null) return null;
    if (renderIcon) return renderIcon({ isOpen: menuOpen });

    return <KeyboardArrowDownIcon htmlColor={isDisabled ? '#aa9fa2' : '#2E2E38'} />;
  };

  render() {
    const {
      className,
      isDisabled,
      isLoading,
      isClearable,
      isSearchable,
      label,
      classes,
      hasError,
      openOnFocus,
      openMenuOnFocus,
      openOnClick,
      options,
      required,
      usePortal,
    } = this.props;
    const { isFocused, value } = this.state;
    const selectedOption = this.getSelectedOption();
    const shouldShrinkLabel = isFocused || !isEmptyValue(value);
    const resolvedOpenOnFocus = typeof openMenuOnFocus === 'undefined' ? openOnFocus : openMenuOnFocus;
    const rootClass = classNames(
      {
        [classes.root]: true,
        [classes.focused]: isFocused,
        [classes.hasError]: hasError,
        [classes.enableDropdownField]: !isDisabled,
      },
      className,
    );

    return (
      <div className={rootClass}>
        <div className={classes.labelPlaceholder} />
        <Autocomplete
          className={classes.autocomplete}
          classes={{
            paper: classes.paper,
            listbox: classes.listbox,
            option: classes.option,
            popper: classes.popper,
            popupIndicator: classes.popupIndicator,
            popupIndicatorOpen: classes.popupIndicatorOpen,
            noOptions: classes.noOptions,
            loading: classes.noOptions,
          }}
          disablePortal={!usePortal}
          disabled={isDisabled}
          disableClearable={!isClearable}
          filterOptions={this.filterOptions}
          forcePopupIcon
          getOptionKey={this.getOptionKey}
          getOptionLabel={this.getOptionLabel}
          isOptionEqualToValue={(option, selectedValue) => valuesEqual(this.getOptionValue(option), this.getOptionValue(selectedValue))}
          loading={isLoading}
          loadingText={intl.get('autocomplete.loading') || 'Loading...'}
          noOptionsText={intl.get('autocomplete.noResults')}
          onBlur={event => this.toggleFocus(false, event)}
          onChange={this.handleChange}
          onClose={this.handleMenuClose}
          onFocus={event => this.toggleFocus(true, event)}
          onInputChange={this.handleInputChange}
          onOpen={this.handleMenuOpen}
          openOnFocus={Boolean(resolvedOpenOnFocus)}
          options={options || []}
          popupIcon={this.renderPopupIcon()}
          readOnly={!isSearchable}
          renderInput={params => (
            <TextField
              {...params}
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
              inputProps={{
                ...params.inputProps,
                readOnly: !isSearchable,
                'aria-label': label,
              }}
            />
          )}
          renderOption={this.renderOption}
          selectOnFocus={isSearchable}
          value={selectedOption}
        />
        <label
          className={classNames({
            [classes.floatLabel]: true,
            [classes.disabled]: isDisabled,
            [classes.floatLabelShrink]: shouldShrinkLabel,
            [classes.mobileShrinkLabel]: (isMobile || isTablet) && shouldShrinkLabel,
          })}>
          {label}
          {required && intl.get('questionnaire.requiredLabel')}
        </label>
      </div>
    );
  }
}

export { DropdownBase };
export default styled(DropdownBase)(styles);
